<template>
  <div>
    <v-dialog
      v-model="showDialog"
      persistent
      max-width="600px"
    >
      <v-card>
        <validation-observer
          v-slot="{ handleSubmit }"
        >
          <form @submit.prevent="handleSubmit(submitForm)">
            <v-card-text>
              <v-card-title>
                <span class="headline">{{ title }}</span>
              </v-card-title>
              <v-container>
                <v-row>
                  <v-col cols="12">
                    <v-skeleton-loader
                      class="mx-auto"
                      max-width="300"
                      type="card"
                      :loading="loadingAlert"
                    >
                      <v-row>
                        <v-col
                          cols="12"
                          md="9"
                        >
                          <validation-provider
                            v-slot="{ errors }"
                            rules="required|max:250"
                            name="alert.description"
                          >
                            <v-text-field
                              v-model="alert.description"
                              :error-messages="errors"
                              color="secondary"
                              :label="$t('fields.alert.description')"
                              outlined
                            />
                          </validation-provider>
                        </v-col>
                        <v-col
                          cols="12"
                          md="3"
                        >
                          <validation-provider
                            v-slot="{ errors }"
                            rules="required"
                            name="alert.active"
                          >
                            <v-switch
                              v-model="alert.active"
                              class="mx-2"
                              :label="$t('fields.alert.active')"
                              :error-messages="errors"
                            />
                          </validation-provider>
                        </v-col>
                        <v-col
                          cols="12"
                          md="6"
                        >
                          <validation-provider
                            v-slot="{ errors }"
                            rules="required"
                            name="alert.contactId"
                          >
                            <v-select
                              v-model="alert.contacts"
                              :items="contacts"
                              item-text="name"
                              item-value="id"
                              :label="$t('fields.alert.contacts')"
                              :error-messages="errors"
                              outlined
                              multiple
                              :loading="loadingContacts"
                            />
                          </validation-provider>
                        </v-col>
                        <v-col
                          cols="12"
                          md="6"
                        >
                          <validation-provider
                            v-slot="{ errors }"
                            rules="required"
                            name="alert.type"
                          >
                            <v-select
                              v-model.number="alert.periodicity"
                              :items="periodicities"
                              item-text="text"
                              item-value="value"
                              :label="$t('fields.alert.periodicity')"
                              :error-messages="errors"
                              outlined
                            />
                          </validation-provider>
                        </v-col>
                      </v-row>
                      <v-row v-if="alert.type == 'Temp'">
                        <v-col
                          cols="12"
                          md="6"
                        >
                          <validation-provider
                            v-slot="{ errors }"
                            rules="required"
                            name="alert.alertTypeTemp"
                          >
                            <v-radio-group
                              v-model="alert.alertTypeTemp"
                              :mandatory="true"
                              :error-messages="errors"
                            >
                              <template v-slot:label>
                                <div>{{ $t('fields.alert.alertType') }}</div>
                              </template>
                              <v-radio
                                :label="$t('fields.alert.min')"
                                value="Min"
                              />
                              <v-radio
                                :label="$t('fields.alert.max')"
                                value="Max"
                              />
                              <v-radio
                                :label="$t('fields.alert.isMinMax')"
                                value="MinMax"
                              />
                            </v-radio-group>
                          </validation-provider>
                        </v-col>
                      </v-row>
                      <v-row v-if="alert.type == 'Tank'">
                        <v-col
                          cols="12"
                          md="6"
                        >
                          <validation-provider
                            v-slot="{ errors }"
                            rules="required"
                            name="alert.alertTypeTank"
                          >
                            <v-radio-group
                              v-model="alert.alertTypeTank"
                              :mandatory="true"
                              :error-messages="errors"
                            >
                              <template v-slot:label>
                                <div>{{ $t('fields.alert.alertType') }}</div>
                              </template>
                              <v-radio
                                :label="$t('fields.alert.min')"
                                value="Min"
                              />
                            </v-radio-group>
                          </validation-provider>
                        </v-col>
                      </v-row>
                    </v-skeleton-loader>
                  </v-col>
                </v-row>
              </v-container>
            </v-card-text>
            <v-card-actions>
              <v-spacer />
              <v-btn
                color="warning"
                depressed
                rounded
                @click="close"
              >
                {{ $t('cancel') }}
              </v-btn>
              <v-btn
                color="info"
                depressed
                rounded
                type="submit"
              >
                {{ $t('save') }}
              </v-btn>
            </v-card-actions>
          </form>
        </validation-observer>
      </v-card>
    </v-dialog>
    <base-material-snackbar
      v-model="showErrorMessage"
      type="error"
      v-bind="{
        ['top']: true,
        ['center']: true,
        ['vertical']: true
      }"
    >
      {{ errorMessage }}
    </base-material-snackbar>
  </div>
</template>

<script>
  import mqtt from 'mqtt'
  import { repositoryFactory } from '@/api/repositoryFactory'
  const contactsRespository = repositoryFactory.get('persons')
  const targetAlertsRespository = repositoryFactory.get('targetAlerts')

  export default {
    inject: ['theme'],
    name: 'AlertCrud',
    $_veeValidate: {
      validator: 'new',
    },
    props: {
      show: {
        type: Boolean,
        default: false,
      },
      targetAlertId: {
        type: String,
        default: '',
      },
      targetDeviceId: {
        type: String,
        default: '',
      },
      method: {
        type: String,
        default: '',
      },
      alertType: {
        type: String,
        default: '',
      },
    },
    data: () => ({
      emptyAlert: { active: true, type: '', contacts: [] },
      alert: { active: true, type: '', contacts: [] },
      periodicities: [{ text: '5m', value: 5 },
                      { text: '10m', value: 10 },
                      { text: '15m', value: 15 },
                      { text: '30m', value: 30 },
                      { text: '45m', value: 45 },
                      { text: '1h', value: 60 },
                      { text: '1:30h', value: 90 },
                      { text: '2h', value: 120 },
                      { text: '3h', value: 180 },
                      { text: '4h', value: 240 },
                      { text: '5h', value: 300 },
                      { text: '6h', value: 360 },
                      { text: '12h', value: 720 },
                      { text: '24h', value: 1440 }],
      contacts: [],
      showErrorMessage: false,
      errorMessage: '',
      loadingAlert: false,
      loadingContacts: false,
      starOnDateDateMenu: false,
      endOnDateDateMenu: false,
      startTimeMenu: false,
      endTimeModal: false,
      showDialog: false,
      connection: {
        host: 'mqtt.multisensores.com',
        port: 8084,
        endpoint: '/mqtt',
        clean: true, // Reserved session
        connectTimeout: 4000, // Time out
        reconnectPeriod: 4000, // Reconnection interval
        // Certification Information
        clientId: 'alert_view_temp',
        username: 'alert_view_temp_user',
        password: 'alert_view_temp_pass',
      },
      client: {
        connected: false,
      },
    }),
    computed: {
      title () {
        return this.method === 'add' ? this.$t('add-alert') : this.$t('modify-alert')
      },
    },
    watch: {
      show (val) {
        this.showDialog = val
        if (val === true) {
          this.fetchData()
        }
      },
    },
    mounted () {
      this.createConnection()
    },
    methods: {
      fetchData () {
        this.alert.type = this.alertType
        var self = this
        if (self.method !== 'add') {
          self.loadingAlert = true
          var id = self.targetAlertId
          targetAlertsRespository.getById(id).then(response => {
            self.loadingAlert = false
            self.alert = Object.assign({}, response.data.data)
          }).catch(err => {
            self.loadingAlert = false
            alert(err)
          })
        }

        this.getContacts()
      },
      getContacts () {
        var self = this
        self.loadingContacts = true
        contactsRespository.get().then(response => {
          self.contacts = response.data.data
          self.loadingContacts = false
        }).catch(err => {
          self.loadingContacts = false
          self.showErrorMessage = true
          self.errorMessage = err
        })
      },
      submitForm (scope) {
        var self = this
        var data = {
          data: this.alert,
        }

        if (self.method !== 'add') {
          targetAlertsRespository.update(data, this.alert.id).then(response => {
            if (response.data.isValid) {
              self.showSuccessAlert = true
              self.close()
              self.client.publish('terrapez/alerts', 'update')
            } else {
              self.showErrorMessage = true
              self.errorMessage = response.data.errorMessages[0]
            }
          }).catch(err => {
            self.errorMessage = err
            self.showErrorMessage = true
          })
        } else {
          data.data.targetDeviceId = self.targetDeviceId
          targetAlertsRespository.add(data).then(response => {
            if (response.data.isValid) {
              self.alert = response.data.data
              self.showSuccessAlert = true
              self.close()
              self.client.publish('terrapez/alerts', 'update')
            } else {
              self.showErrorMessage = true
              self.errorMessage = response.data.errorMessages[0]
            }
          }).catch(err => {
            self.errorMessage = err
            self.showErrorMessage = true
          })
        }
      },
      close () {
        this.$emit('change-edit-dialog', false)
        this.showDialog = false
        this.$nextTick(() => {
          this.alert = Object.assign({}, this.emptyAlert)
        })
      },
      createConnection () {
        // Connect string, and specify the connection method used through protocol
        // ws unencrypted WebSocket connection
        // wss encrypted WebSocket connection
        // mqtt unencrypted TCP connection
        // mqtts encrypted TCP connection
        // wxs WeChat mini app connection
        // alis Alipay mini app connection
        this.connection.clientId = 'alert_view_temp_' + Math.round(Math.random() * (0 - 10000) * -1)

        const { host, port, endpoint, ...options } = this.connection
        const connectUrl = `wss://${host}:${port}${endpoint}`
        try {
          this.client = mqtt.connect(connectUrl, options)
        } catch (error) {
          console.log('mqtt.connect error', error)
        }
        this.client.on('connect', () => {
          console.log('Connection succeeded!')
        })
        this.client.on('error', error => {
          console.log('Connection failed', error)
        })
      },
    },
  }
</script>
